import { useMemo, useState, useEffect } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// ReportsLineChart configurations
import configs from "examples/Charts/LineCharts/ReportsLineChart/configs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function ReportsLineChart({
  color,
  title,
  chart,
  chartHeight,
  timestamp,
  cardSx,
  chartWrapperSx,
  lineBoxSx,
  contentSx,
  showCard,
  checkboxItems,
  rightCheckboxItems,
  showMinDeviation,
  minDeviationLabel,
  minDeviationValue,
  onMinDeviationChange,
  onCheckedItemsChange,
  defaultEnabled,
  onDefaultEnabledChange,
  selectorsBelow,
  facilityOptions,
  cycleOptions,
  selectedFacilityId,
  selectedCycleId,
  farmOptions,
  selectedFarmId,
  onFarmChange,
  onFacilityChange,
  onCycleChange,
  onPointClick,
  xAxisTickRotation,
}) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {}, {
    onPointClick,
    xAxisTickRotation,
  });
  const [internalMinDeviationValue, setInternalMinDeviationValue] = useState(0);
  const minDeviationOptions = useMemo(() => Array.from({ length: 101 }, (_, idx) => idx), []);
  const resolvedMinDeviation =
    minDeviationValue !== undefined && minDeviationValue !== null
      ? minDeviationValue
      : internalMinDeviationValue;
  const handleMinDeviationChange =
    onMinDeviationChange ??
    ((value) => {
      setInternalMinDeviationValue(value);
    });
  const handleMinDeviationValueChange = (value) => {
    if (value === "" || value === null || value === undefined) {
      handleMinDeviationChange(0);
      return;
    }
    const next = Number(value);
    if (!Number.isFinite(next)) return;
    handleMinDeviationChange(Math.round(next));
  };
  const isDefaultMinDeviation =
    defaultEnabled !== undefined ? defaultEnabled : Number(resolvedMinDeviation) !== 0;
  const allCheckboxItems = useMemo(
    () => [
      ...(Array.isArray(checkboxItems) ? checkboxItems : []),
      ...(Array.isArray(rightCheckboxItems) ? rightCheckboxItems : []),
    ],
    [checkboxItems, rightCheckboxItems]
  );
  const checkboxItemsKey = useMemo(() => allCheckboxItems.join("|"), [allCheckboxItems]);
  const [checkedItems, setCheckedItems] = useState(() => {
    const next = {};
    if (Array.isArray(allCheckboxItems)) {
      allCheckboxItems.forEach((item) => {
        if (item === "Minimum Deviation %") return;
        next[item] = true;
      });
    }
    return next;
  });

  useEffect(() => {
    if (!Array.isArray(allCheckboxItems)) return;
    setCheckedItems((prev) => {
      const next = { ...prev };
      allCheckboxItems.forEach((item) => {
        if (item === "Minimum Deviation %") return;
        if (next[item] === undefined) next[item] = true;
      });
      Object.keys(next).forEach((key) => {
        if (!allCheckboxItems.includes(key)) delete next[key];
      });
      return next;
    });
  }, [checkboxItemsKey]);

  useEffect(() => {
    onCheckedItemsChange?.(checkedItems);
  }, [checkedItems, onCheckedItemsChange]);

  return (
    <Card
      sx={{
        height: "100%",
        ...(showCard ? {} : { boxShadow: "none", backgroundColor: "transparent" }),
        ...(cardSx || {}),
      }}
    >
      <MDBox padding={showCard ? "1rem" : 0} sx={{ position: "relative", ...(contentSx || {}) }}>
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              bgColor={color}
              borderRadius="lg"
              coloredShadow={color}
              py={2}
              pr={0.5}
              mt={-5}
              height={chartHeight}
              sx={chartWrapperSx}
            >
              <MDBox sx={{ height: "100%", ...lineBoxSx }}>
                <Line data={data} options={options} />
              </MDBox>
            </MDBox>
          ),
          [chart, color, chartWrapperSx, lineBoxSx, data, options, chartHeight]
        )}
        {showMinDeviation || (Array.isArray(checkboxItems) && checkboxItems.length > 0) ? (
          <MDBox
            sx={{
              position: "absolute",
              left: "1rem",
              bottom: "calc(0.75rem - 1.5cm + 2cm)",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.5,
            }}
          >
            {showMinDeviation ? (
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                  {minDeviationLabel}
                </MDTypography>
                <Select
                  size="small"
                  value={resolvedMinDeviation}
                  onChange={(e) => handleMinDeviationValueChange(e.target.value)}
                  sx={{
                    minWidth: "2.25rem",
                    "& .MuiSelect-select": { py: 0.5, fontSize: "0.95rem" },
                  }}
                >
                  {minDeviationOptions.map((value) => (
                    <MenuItem key={value} value={value} sx={{ fontSize: "0.95rem" }}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={isDefaultMinDeviation}
                      onChange={(e) => {
                        const nextChecked = e.target.checked;
                        if (onDefaultEnabledChange) {
                          onDefaultEnabledChange(nextChecked);
                          if (!nextChecked) handleMinDeviationValueChange(0);
                        } else if (!nextChecked) {
                          handleMinDeviationValueChange(0);
                        }
                      }}
                    />
                  }
                  label={
                    <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                      Default
                    </MDTypography>
                  }
                  sx={{
                    m: 0,
                    ml: "0.2cm",
                    "& .MuiFormControlLabel-label": { lineHeight: 1.3 },
                    "& .MuiCheckbox-root": { p: 0.5 },
                  }}
                />
              </MDBox>
            ) : null}
            {Array.isArray(checkboxItems) && checkboxItems.length > 0 ? (
              <FormGroup
                sx={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1.25,
                  ml: "-0.5cm",
                }}
                row
              >
                {checkboxItems
                  .filter((item) => item !== "Minimum Deviation %")
                  .map((item) => (
                    <FormControlLabel
                      key={item}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={checkedItems[item] ?? true}
                          onChange={(e) =>
                            setCheckedItems((prev) => ({
                              ...prev,
                              [item]: e.target.checked,
                            }))
                          }
                        />
                      }
                      label={
                        <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                          {item}
                        </MDTypography>
                      }
                      sx={{
                        m: 0,
                        ml: item === "Mortality" || item === "Feed" || item === "FCR" ? "0.5cm" : 0,
                        "& .MuiFormControlLabel-label": { lineHeight: 1.3 },
                        "& .MuiCheckbox-root": { p: 0.5 },
                      }}
                    />
                  ))}
              </FormGroup>
            ) : null}
          </MDBox>
        ) : null}
        {Array.isArray(rightCheckboxItems) && rightCheckboxItems.length > 0 ? (
          <MDBox
            sx={{
              position: "absolute",
              right: "1rem",
              bottom: "calc(0.75rem - 1.5cm + 2cm)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <FormGroup
              sx={{
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.25,
              }}
              row
            >
              {rightCheckboxItems.map((item) => (
                <FormControlLabel
                  key={item}
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={checkedItems[item] ?? true}
                      onChange={(e) =>
                        setCheckedItems((prev) => ({
                          ...prev,
                          [item]: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={
                    <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                      {item}
                    </MDTypography>
                  }
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": { lineHeight: 1.3 },
                    "& .MuiCheckbox-root": { p: 0.5 },
                  }}
                />
              ))}
            </FormGroup>
          </MDBox>
        ) : null}
        {(Array.isArray(farmOptions) && farmOptions.length > 0) ||
        (Array.isArray(facilityOptions) && facilityOptions.length > 0) ||
        (Array.isArray(cycleOptions) && cycleOptions.length > 0) ? (
          <MDBox
            sx={{
              position: selectorsBelow ? "static" : "absolute",
              right: selectorsBelow ? "auto" : "1rem",
              bottom: selectorsBelow ? "auto" : "calc(0.75rem - 1.5cm)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              mt: selectorsBelow ? "1cm" : 0,
            }}
          >
            {Array.isArray(farmOptions) && farmOptions.length > 0 ? (
              <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                  Farm
                </MDTypography>
                <Select
                  size="small"
                  value={selectedFarmId ?? ""}
                  onChange={(e) => onFarmChange?.(e.target.value)}
                  sx={{
                    minWidth: "7rem",
                    "& .MuiSelect-select": { py: 0.5, fontSize: "0.95rem" },
                  }}
                >
                  {farmOptions.map((option) => (
                    <MenuItem
                      key={option?.id ?? option?.name ?? option}
                      value={option?.id ?? option}
                      sx={{ fontSize: "0.95rem" }}
                    >
                      {option?.name ?? option}
                    </MenuItem>
                  ))}
                </Select>
              </MDBox>
            ) : null}
            {Array.isArray(facilityOptions) && facilityOptions.length > 0 ? (
              <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                  Facility
                </MDTypography>
                <Select
                  size="small"
                  value={selectedFacilityId ?? ""}
                  onChange={(e) => onFacilityChange?.(e.target.value)}
                  sx={{
                    minWidth: "7rem",
                    "& .MuiSelect-select": { py: 0.5, fontSize: "0.95rem" },
                  }}
                >
                  {facilityOptions.map((option) => (
                    <MenuItem
                      key={option?.id ?? option?.name ?? option}
                      value={option?.id ?? option}
                      sx={{ fontSize: "0.95rem" }}
                    >
                      {option?.name ?? option}
                    </MenuItem>
                  ))}
                </Select>
              </MDBox>
            ) : null}
            {Array.isArray(cycleOptions) && cycleOptions.length > 0 ? (
              <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                  Cycle
                </MDTypography>
                <Select
                  size="small"
                  value={selectedCycleId ?? ""}
                  onChange={(e) => onCycleChange?.(e.target.value)}
                  sx={{
                    minWidth: "6rem",
                    "& .MuiSelect-select": { py: 0.5, fontSize: "0.95rem" },
                  }}
                >
                  {cycleOptions.map((option) => (
                    <MenuItem
                      key={option?.id ?? option?.name ?? option}
                      value={option?.id ?? option}
                      sx={{ fontSize: "0.95rem" }}
                    >
                      {option?.name ?? option}
                    </MenuItem>
                  ))}
                </Select>
              </MDBox>
            ) : null}
          </MDBox>
        ) : null}
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ReportsLineChart
ReportsLineChart.defaultProps = {
  color: "info",
  description: "",
  canvasHeight: "12.5rem",
  cardSx: {},
  chartWrapperSx: {},
  lineBoxSx: {},
  contentSx: {},
  showCard: true,
  checkboxItems: [],
  rightCheckboxItems: [],
  showMinDeviation: false,
  minDeviationLabel: "Minimum Deviation %",
  minDeviationValue: undefined,
  onMinDeviationChange: undefined,
  onCheckedItemsChange: undefined,
  defaultEnabled: undefined,
  onDefaultEnabledChange: undefined,
  selectorsBelow: false,
  facilityOptions: [],
  cycleOptions: [],
  selectedFacilityId: "",
  selectedCycleId: "",
  farmOptions: [],
  selectedFarmId: "",
  onFarmChange: undefined,
  onFacilityChange: undefined,
  onCycleChange: undefined,
  onPointClick: undefined,
  xAxisTickRotation: undefined,
};

// Typechecking props for the ReportsLineChart
ReportsLineChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  chartHeight: PropTypes.string,
  timestamp: PropTypes.string,
  cardSx: PropTypes.object,
  chartWrapperSx: PropTypes.object,
  lineBoxSx: PropTypes.object,
  contentSx: PropTypes.object,
  showCard: PropTypes.bool,
  checkboxItems: PropTypes.arrayOf(PropTypes.string),
  rightCheckboxItems: PropTypes.arrayOf(PropTypes.string),
  showMinDeviation: PropTypes.bool,
  minDeviationLabel: PropTypes.string,
  minDeviationValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onMinDeviationChange: PropTypes.func,
  onCheckedItemsChange: PropTypes.func,
  defaultEnabled: PropTypes.bool,
  onDefaultEnabledChange: PropTypes.func,
  selectorsBelow: PropTypes.bool,
  facilityOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      PropTypes.string,
    ])
  ),
  cycleOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      PropTypes.string,
    ])
  ),
  farmOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      PropTypes.string,
    ])
  ),
  selectedFarmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedFacilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedCycleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFarmChange: PropTypes.func,
  onFacilityChange: PropTypes.func,
  onCycleChange: PropTypes.func,
  onPointClick: PropTypes.func,
  xAxisTickRotation: PropTypes.number,
};

export default ReportsLineChart;
